import { create } from "zustand";

export type SectionId =
  | "journals-info"
  | "developer-company"
  | "tec-customer-company"
  | "building-contractor-company"
  | "documentation-contractor-company"
  | "designer-supervisions"
  | "other-developers"
  | "documentation-permission"
  | "documentation-examination";

interface TitleSectionsStore {
  sections: Record<SectionId, boolean>;
  openedSections: Record<SectionId, boolean>;
  activeSectionId: SectionId | null;
  currentErrorIndex: number;
  toggleSection: (id: SectionId) => void;
  isSectionOpened: (id: SectionId) => boolean;
  resetSections: () => void;
  setOpenedSection: (id: SectionId, isOpen: boolean) => void;
  isCurrentlyOpen: (id: SectionId) => boolean;
  openSections: (sectionIds: SectionId[]) => void;
  setActiveSection: (id: SectionId | null) => void;
  getActiveSection: () => SectionId | null;
  setCurrentErrorIndex: (index: number) => void;
  nextError: () => void;
  resetErrorIndex: () => void;
}

const initialSections: Record<SectionId, boolean> = {
  "journals-info": false,
  "developer-company": false,
  "tec-customer-company": false,
  "building-contractor-company": false,
  "documentation-contractor-company": false,
  "designer-supervisions": false,
  "other-developers": false,
  "documentation-permission": false,
  "documentation-examination": false,
};

const initialOpenedSections: Record<SectionId, boolean> = {
  "journals-info": false,
  "developer-company": false,
  "tec-customer-company": false,
  "building-contractor-company": false,
  "documentation-contractor-company": false,
  "designer-supervisions": false,
  "other-developers": false,
  "documentation-permission": false,
  "documentation-examination": false,
};

export const useTitleSectionsStore = create<TitleSectionsStore>((set, get) => ({
  sections: initialSections,
  openedSections: initialOpenedSections,
  activeSectionId: null,
  currentErrorIndex: 0,

  toggleSection: (id: SectionId) => {
    set((state) => ({
      sections: {
        ...state.sections,
        [id]: true,
      },
    }));
  },

  isSectionOpened: (id: SectionId) => {
    return get().sections[id];
  },

  setOpenedSection: (id: SectionId, isOpen: boolean) => {
    set((state) => ({
      openedSections: {
        ...state.openedSections,
        [id]: isOpen,
      },
    }));
  },

  isCurrentlyOpen: (id: SectionId) => {
    return get().openedSections[id];
  },

  resetSections: () => {
    set({ sections: initialSections, openedSections: initialOpenedSections, activeSectionId: null, currentErrorIndex: 0 });
  },

  openSections: (sectionIds: SectionId[]) => {
    set((state) => {
      const newOpenedSections = { ...state.openedSections };
      sectionIds.forEach((id) => {
        newOpenedSections[id] = true;
      });
      const firstSection = sectionIds[0] || null;
      return { openedSections: newOpenedSections, activeSectionId: firstSection };
    });
  },

  setActiveSection: (id: SectionId | null) => {
    set({ activeSectionId: id });
  },

  getActiveSection: () => {
    return get().activeSectionId;
  },

  setCurrentErrorIndex: (index: number) => {
    set({ currentErrorIndex: index });
  },

  nextError: () => {
    set((state) => ({
      currentErrorIndex: state.currentErrorIndex + 1,
    }));
  },

  resetErrorIndex: () => {
    set({ currentErrorIndex: 0 });
  },
}));

